import { createContext, KeyboardEvent, useContext, useRef } from 'react';

// True inside the popover calendar, false inline. When true, every control is
// out of the page tab order (tabIndex={-1}): focus enters via the arrow keys and
// Tab is trapped within the popover. Inline has no input to arrow in from, so it
// stays normally tabbable. Read by the day buttons, header, and month/year grids.
export const ArrowNavContext = createContext(false);

// Arrow-key navigation for the 3-column month/year grids (react-day-picker
// brings this for the day grid; our grids don't). Every cell is tabIndex={-1},
// so focus enters via the arrow keys and then moves within the grid: Left/Right
// by 1, Up/Down by a row (GRID_COLUMNS), Home/End to the first/last enabled cell.
// Movement is relative to the cell that received the keydown, and disabled cells
// are skipped (a disabled <button> can't hold focus).
export const GRID_COLUMNS = 3;

// Arrow key → cell offset: ±1 within a row, ±a row vertically.
const GRID_KEY_DELTAS: Record<string, number> = {
  ArrowRight: 1,
  ArrowLeft: -1,
  ArrowDown: GRID_COLUMNS,
  ArrowUp: -GRID_COLUMNS,
};

export const useGridArrowNav = (disabled: boolean[]) => {
  const count = disabled.length;
  const arrowNav = useContext(ArrowNavContext);
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  // Step from `start` by `delta`, skipping disabled cells; no-op if the run
  // hits an edge without landing on an enabled cell.
  const moveBy = (start: number, delta: number) => {
    let next = start + delta;
    while (next >= 0 && next < count && disabled[next]) next += delta;
    if (next < 0 || next >= count || disabled[next]) return;
    refs.current[next]?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key === 'Home') {
      event.preventDefault();
      return moveBy(-1, 1);
    }
    if (event.key === 'End') {
      event.preventDefault();
      return moveBy(count, -1);
    }
    if (!(event.key in GRID_KEY_DELTAS)) return;
    event.preventDefault();
    moveBy(index, GRID_KEY_DELTAS[event.key]);
  };

  return (index: number) => ({
    ref: (el: HTMLButtonElement | null) => {
      refs.current[index] = el;
    },
    // In the popover the grid is out of the page tab order (entered by arrow,
    // Tab trapped); inline keeps the cells tabbable.
    tabIndex: arrowNav ? -1 : 0,
    onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => handleKeyDown(event, index),
  });
};
